import { AfterViewInit, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core'
import {
  DEFAULT_DATE_FORMAT,
  DateRange,
  formatDate,
  formatDateIntoDayJs,
  formatDateIntoUTC,
} from './scope-ui-datepicker.const'
import { DaterangepickerComponent, DaterangepickerDirective, NgxDaterangepickerMd } from 'ngx-daterangepicker-material'
import { SNACKBAR_LENGTH_LONG, SnackbarEventType, SnackbarService } from '@app/shared/utils/snackbar.service'
import dayjs, { ConfigType, Dayjs } from 'dayjs'

import { AuthService } from '@core/service/auth.service'
import { ChosenDate } from 'ngx-daterangepicker-material/daterangepicker.component'
import { FormControl } from '@angular/forms'
import { NgIf } from '@angular/common'
import { SharedModule } from '@shared/shared.module'
import isBetween from 'dayjs/plugin/isBetween'
import { isString } from 'lodash'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
dayjs.extend(isBetween)

@Component({
  selector: 'scope-ui-datepicker',
  standalone: true,
  templateUrl: './scope-ui-datepicker.component.html',
  imports: [NgxDaterangepickerMd, NgIf, SharedModule],
  styleUrls: ['./scope-ui-datepicker.component.scss'],
})
export class ScopeUiDatepickerComponent implements AfterViewInit {
  @Input() dateControl: FormControl = new FormControl<string | any>('')
  @Input() singleDatePicker?: boolean
  @Input() inlineDatePicker?: boolean
  @Input() inputLabel!: string
  @Input() inputPlaceholder: string
  @Input() minDateClass?: string
  @Input() maxDateClass?: string
  @Input() validationError?: string
  @Input() opens?: string = 'auto'
  @Input() drops?: string = 'down'
  @Input() set disabled(value: boolean) {
    this.isDisabled = value
    this.setDisabledState()
  }
  @Input() set initialValue(value: Date | DateRange | string) {
    this.setInitialValue(value)
  }
  @Input() set minDate(value: Date) {
    this.setMinDate(value)
  }
  @Input() set maxDate(value: Date) {
    this.setMaxDate(value)
  }
  @Input() set formSubmitted(value: boolean) {
    if (value) {
      this.dateControl.updateValueAndValidity()
      this.dateControl.markAsTouched()
    }
  }

  @Output() onSelect = new EventEmitter<any>()

  @ViewChild(DaterangepickerComponent) datePicker!: DaterangepickerComponent
  @ViewChild(DaterangepickerDirective) datePickerDirective!: DaterangepickerDirective

  minDateValue: Dayjs
  maxDateValue: Dayjs
  isDisabled = false

  private _initialValue: Dayjs | DateRange

  constructor(
    private authService: AuthService,
    private snackbarService: SnackbarService
  ) {}

  ngAfterViewInit() {
    this.validateInitialValue()
    this.setDisabledState()
  }

  setInitialValue(value: typeof this.initialValue) {
    if (typeof value === 'string') value = new Date(value)
    if (value) {
      this._initialValue =
        value instanceof Date
          ? dayjs.utc(value)
          : { startDate: dayjs.utc(value.startDate), endDate: dayjs.utc(value.endDate) }
      this.validateInitialValue()
    }
  }

  validateInitialValue() {
    if (!this._initialValue) return this.datePickerDirective?.clear()

    if (this.singleDatePicker) {
      if (this.inlineDatePicker) return this.setDatePickerStartEndDates(this._initialValue as Dayjs)
      else {
        if (this._initialValue < this.minDateValue) this._initialValue = this.minDateValue
        if (this._initialValue > this.maxDateValue) this._initialValue = this.maxDateValue

        return this.setInitialControlValue()
      }
    }

    if (this.datePicker) {
      const { startDate, endDate } = this._initialValue as DateRange
      return this.setDatePickerStartEndDates(startDate, endDate)
    }
  }

  isCustomDate = (date: Dayjs): any => {
    if (this.getDayJsDate(date) === this.getDayJsDate(this.minDateValue)) return this.minDateClass
    if (this.getDayJsDate(date) === this.getDayJsDate(this.maxDateValue)) return this.maxDateClass
    if (
      (date.get('month') < this.minDateValue?.get('month') && date.get('year') === this.minDateValue?.get('year')) ||
      (date.get('month') > this.maxDateValue?.get('month') && date.get('year') === this.maxDateValue?.get('year'))
    )
      return 'invisible'

    return false
  }

  onSelectedChange(date: ChosenDate) {
    this.onSelect.emit({
      startDate: new Date(this.getDayJsDate(date.startDate as Dayjs)),
      endDate: new Date(this.getDayJsDate(date.endDate as Dayjs)),
    })
  }

  onChange($event: any) {
    const selectedDate = $event.startDate ? this.getDate($event.startDate) : $event.target?.value
    let date = selectedDate ? this.getDayJsDate(selectedDate) : null

    if (!selectedDate || !date || !dayjs.utc(date).isValid()) return this.onSelect.emit()

    if (this.minDateValue && dayjs.utc(date).isBefore(this.minDateValue)) {
      date = this.getDayJsDate(this.minDateValue)
      this.showError()
    }

    if (this.maxDateValue && dayjs.utc(date).isAfter(this.maxDateValue)) {
      date = this.getDayJsDate(this.maxDateValue)
      this.showError()
    }

    if (!this._initialValue || date !== this.getDayJsDate(this._initialValue as Dayjs))
      this.onSelect.emit(new Date(date))
  }

  showError() {
    if (this.validationError)
      this.snackbarService.showSnackbar(this.validationError, SNACKBAR_LENGTH_LONG, SnackbarEventType.ERROR)
  }

  private setDisabledState() {
    if (this.isDisabled) this.dateControl.disable()
    else this.dateControl.enable()
  }

  private setInitialControlValue() {
    this.dateControl.setValue({ startDate: this._initialValue, endDate: this._initialValue })
  }

  private setMaxDate(value: typeof this.minDate) {
    if (value && value instanceof Date) {
      this.maxDateValue = dayjs.utc(this.getUtcDate(value))
      if (this._initialValue && this._initialValue > this.maxDateValue) {
        this._initialValue = this.maxDateValue
        this.setInitialControlValue()
      }
    }
  }

  private setMinDate(value: typeof this.minDate) {
    if (value && value instanceof Date && !isNaN(value.getDay())) {
      this.minDateValue = dayjs.utc(this.getUtcDate(value))
      if (this._initialValue && this._initialValue < this.minDateValue) {
        this._initialValue = this.minDateValue
        this.setInitialControlValue()
      }
    }
  }

  private setDatePickerStartEndDates(startDate, endDate = startDate) {
    this.datePicker.setStartDate(dayjs.utc(startDate))
    this.datePicker.setEndDate(dayjs.utc(endDate))
    this.datePicker.updateView()
  }

  // this.locale.format
  private getDate = (date: ConfigType): string => formatDate(date, this.locale.format)

  // YYYY-MM-DD
  private getDayJsDate(date: ConfigType): string {
    if (!isString(date)) date = this.getDate(date)
    return formatDateIntoDayJs(date, this.locale.format)
  }

  // UTC 2025-05-25T00:00:00Z
  private getUtcDate(date: ConfigType): string {
    if (!isString(date)) date = this.getDate(date)
    return formatDateIntoUTC(date, this.locale.format)
  }

  protected readonly locale = {
    format: this.authService.companyDateFormat || DEFAULT_DATE_FORMAT,
    displayFormat: this.authService.companyDateFormat || DEFAULT_DATE_FORMAT,
    daysOfWeek: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
    monthNames: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
  }
}
